import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import courseService from '../services/course.service';
import { slugifyWithFallback } from '../utils/slug';
import { API_BASE_URL } from '../config/api';

const UNIT_PLACEHOLDER = '/images/unit-placeholder.svg';

const UnitThumbnail = ({ thumbnailUrl, title }) => {
  const [loaded, setLoaded] = useState(false);
  const [hadError, setHadError] = useState(false);

  useEffect(() => {
    setLoaded(false);
    setHadError(false);
    if (!thumbnailUrl) {
      return;
    }

    let isActive = true;
    const img = new Image();
    //img.crossOrigin = 'anonymous';
    img.onload = () => {
      if (isActive) {
        setLoaded(true);
      }
    };
    img.onerror = () => {
      if (isActive) {
        setHadError(true);
      }
    };
  img.src = thumbnailUrl;

    return () => {
      isActive = false;
      img.onload = null;
      img.onerror = null;
    };
  }, [thumbnailUrl]);

  const hasThumbnail = Boolean(thumbnailUrl) && !hadError;

  return (
    <div className="relative aspect-video overflow-hidden">
      <img
        src={UNIT_PLACEHOLDER}
        alt="Unit placeholder"
        aria-hidden="true"
        className={`pointer-events-none absolute inset-0 h-full w-full object-cover transition-opacity duration-300 ${
          hasThumbnail && loaded ? 'opacity-0' : 'opacity-100'
        }`}
      />
      {hasThumbnail && (
        <img
          src={thumbnailUrl}
          alt={title || 'Unit thumbnail'}
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-300 ${
            loaded ? 'opacity-100' : 'opacity-0'
          }`}
          loading="lazy"
          onLoad={() => setLoaded(true)}
          onError={(event) => {
            event.currentTarget.src = UNIT_PLACEHOLDER;
            setHadError(true);
          }}
        />
      )}
    </div>
  );
};

const sortByOrder = (items = []) => items.slice().sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0));
const SECTION_PREVIEW_LIMIT = 6;

const API_ORIGIN = (() => {
  try {
    return new URL(API_BASE_URL).origin;
  } catch (error) {
    return '';
  }
})();

const extractUrlCandidate = (value, depth = 0) => {
  if (!value || depth > 4) {
    return '';
  }

  if (typeof value === 'string') {
    return value;
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      const candidate = extractUrlCandidate(item, depth + 1);
      if (candidate) {
        return candidate;
      }
    }
    return '';
  }

  if (typeof value === 'object') {
    const preferredKeys = ['url', 'file', 'path', 'src', 'href', 'secure_url', 'original', 'thumbnail', 'image'];
    for (const key of preferredKeys) {
      if (key in value) {
        const candidate = extractUrlCandidate(value[key], depth + 1);
        if (candidate) {
          return candidate;
        }
      }
    }

    for (const nestedValue of Object.values(value)) {
      const candidate = extractUrlCandidate(nestedValue, depth + 1);
      if (candidate) {
        return candidate;
      }
    }
  }

  return '';
};

const resolveAssetUrl = (value) => {
  if (typeof value !== 'string') {
    value = extractUrlCandidate(value);
  }

  const trimmed = value?.trim?.() ?? '';
  if (!trimmed) {
    return '';
  }

  if (/^(https?:|data:|blob:)/i.test(trimmed)) {
    return trimmed;
  }

  if (trimmed.startsWith('//')) {
    const protocol = typeof window !== 'undefined' && window.location?.protocol ? window.location.protocol : 'https:';
    return `${protocol}${trimmed}`;
  }

  try {
    const base = API_ORIGIN || (typeof window !== 'undefined' && window.location?.origin ? window.location.origin : '');
    return base ? new URL(trimmed, base).href : trimmed;
  } catch (error) {
    return trimmed;
  }
};

const resolveUnitThumbnail = (unit = {}) => {
  const candidates = [
    unit.thumbnail_url,
    unit.thumbnail_image_url,
    unit.thumbnailImageUrl,
    unit.unit_thumbnail_url,
    unit.unitThumbnailUrl,
    unit.thumbnail_file,
    unit.thumbnail,
    unit.thumbnailImage,
    unit.thumbnail_image,
    unit.thumbnailFile,
    unit.thumbnailUrl,
    unit.image_url,
    unit.cover_image_url,
    unit.banner_image_url,
    unit.preview_image_url,
    unit.video_thumbnail_url,
    unit.media_thumbnail_url,
    unit.media?.thumbnail_url,
    unit.media?.thumbnail,
    unit.assets?.thumbnail,
    unit.assets?.preview,
  ];

  const raw = candidates
    .map((value) => (typeof value === 'string' ? value : extractUrlCandidate(value)))
    .find((candidate) => candidate && candidate.trim().length > 0);
  return resolveAssetUrl(raw);
};

const CourseDetailPage = () => {
  const { courseSlug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const initialCourseId = location.state?.courseId || courseService.resolveCourseIdBySlug(courseSlug) || null;
  const [resolvedCourseId, setResolvedCourseId] = useState(initialCourseId);
  const [resolvingCourseId, setResolvingCourseId] = useState(!initialCourseId && Boolean(courseSlug));
  const [loading, setLoading] = useState(!initialCourseId);
  const [error, setError] = useState('');
  const [course, setCourse] = useState(null);
  const [activeSubjectId, setActiveSubjectId] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});

  useEffect(() => {
    if (!courseSlug) {
      return;
    }

    if (location.state?.courseId) {
      setResolvedCourseId(location.state.courseId);
      setResolvingCourseId(false);
      return;
    }

    const cachedId = courseService.resolveCourseIdBySlug(courseSlug);
    if (cachedId) {
      setResolvedCourseId(cachedId);
      setResolvingCourseId(false);
      return;
    }

    let active = true;

    const resolveId = async () => {
      try {
        setResolvingCourseId(true);
        setLoading(true);
        await courseService.getCourses({ include_sections: 1, include_subject_groups: 1 });
        if (!active) {
          return;
        }
        const matchedId = courseService.resolveCourseIdBySlug(courseSlug);
        if (matchedId) {
          setResolvedCourseId(matchedId);
          setError('');
        } else {
          setError('Course not found.');
          setCourse(null);
          setLoading(false);
        }
      } catch (err) {
        if (!active) {
          return;
        }
        console.error('Failed to resolve course slug', err);
        setError('Unable to resolve course right now. Please try again later.');
        setCourse(null);
        setLoading(false);
      } finally {
        if (active) {
          setResolvingCourseId(false);
        }
      }
    };

    resolveId();

    return () => {
      active = false;
    };
  }, [courseSlug, location.state]);

  useEffect(() => {
    let active = true;

    const fetchCourse = async () => {
      try {
        if (!resolvedCourseId) {
          return;
        }

        const cachedCourse = courseService.getCachedCourse(resolvedCourseId);
        if (cachedCourse && Array.isArray(cachedCourse.subject_groups)) {
          if (!active) {
            return;
          }
          setCourse(cachedCourse);
          const subjectGroups = cachedCourse?.subject_groups || [];
          const initialSubject = subjectGroups.find((group) => (group?.sections?.length ?? 0) > 0) || subjectGroups[0];
          setActiveSubjectId(initialSubject?.id ?? null);
          setLoading(false);
          setError('');
          return;
        }

        setLoading(true);
        setError('');
        const detail = await courseService.getCourseDetails(resolvedCourseId, {
          include_sections: 1,
          include_subject_groups: 1,
        });
        if (!active) {
          return;
        }
        setCourse(detail);
        const subjectGroups = detail?.subject_groups || [];
        const initialSubject = subjectGroups.find((group) => (group?.sections?.length ?? 0) > 0) || subjectGroups[0];
        setActiveSubjectId(initialSubject?.id ?? null);
      } catch (err) {
        if (!active) {
          return;
        }
        console.error('Failed to load course details', err);
        setError('Unable to load course details right now. Please try again later.');
        setCourse(null);
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    if (resolvedCourseId) {
      fetchCourse();
    } else if (!resolvingCourseId) {
      setLoading(false);
    }

    return () => {
      active = false;
    };
  }, [resolvedCourseId, resolvingCourseId]);

  const subjects = useMemo(() => {
    if (!course?.subject_groups) {
      return [];
    }
    return course.subject_groups.map((group) => ({
      id: group.id,
      name: group.name || 'Subject',
      sections: sortByOrder(group.sections || []),
    }));
  }, [course]);

  useEffect(() => {
    if (subjects.length === 0) {
      setActiveSubjectId(null);
      return;
    }
    if (!activeSubjectId || !subjects.some((subject) => subject.id === activeSubjectId)) {
      setActiveSubjectId(subjects[0]?.id ?? null);
    }
    setExpandedSections({});
  }, [subjects, activeSubjectId]);

  const activeSubject = subjects.find((subject) => subject.id === activeSubjectId);
  const isSectionExpanded = (sectionKey) => Boolean(expandedSections[sectionKey]);
  const toggleSectionExpansion = (sectionKey) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  };

  return (
    <div className="min-h-screen bg-bg-default">
      <div className="page-container py-8">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="text-sm font-semibold text-primary hover:text-primary/80 mb-4"
        >
          ← Back
        </button>

        {error && (
          <div className="mb-6 rounded-lg border border-error/30 bg-error/10 px-4 py-3 text-sm text-error">
            {error}
          </div>
        )}

        {loading ? (
          <div className="py-24 text-center text-text-secondary">Loading course details...</div>
        ) : !course ? (
          <div className="py-24 text-center text-text-secondary">Course details are not available.</div>
        ) : (
          <div className="space-y-10">
            <div className="space-y-5 rounded-3xl bg-gradient-to-r from-primary via-sky-500 to-blue-600 p-6 text-white shadow-xl sm:p-8">
              <h1 className="text-3xl sm:text-4xl font-bold leading-tight">
                {course.name || 'Untitled Course'}
              </h1>
              {course.description && (
                <p className="text-base leading-relaxed whitespace-pre-line text-white/90">
                  {course.description}
                </p>
              )}
              <div className="flex flex-wrap gap-3">
                {course.class_level && (
                  <span className="inline-flex items-center rounded-full bg-brand-yellow/90 px-3 py-1 text-sm font-semibold text-black shadow-sm shadow-black/10">
                    {course.class_level.toLowerCase().startsWith('class')
                      ? course.class_level
                      : `Class ${course.class_level}`}
                  </span>
                )}
                {Array.isArray(course.exam_details) && course.exam_details.length > 0 && (
                  course.exam_details.map((exam) => {
                    const name = exam?.name || 'Exam';
                    const label = exam?.year ? `${name} ${exam.year}` : name;

                    return (
                      <span
                        key={exam.id || `${exam.name}-${exam.year}`}
                        className="inline-flex items-center rounded-full bg-brand-yellow/90 px-3 py-1 text-sm font-semibold text-black shadow-sm shadow-black/10"
                      >
                        {label}
                      </span>
                    );
                  })
                )}
              </div>
            </div>

            <section className="space-y-6">
              {subjects.length === 0 ? (
                <span className="text-text-secondary">Subjects will be published soon.</span>
              ) : (
                <div className="w-full">
                  <div className="rounded-2xl border border-primary/20 bg-white/80 px-2 py-2 shadow-lg">
                    <div className="flex w-full items-stretch gap-3">
                      {subjects.map((subject) => {
                        const isActive = activeSubjectId === subject.id;
                        return (
                          <button
                            type="button"
                            key={subject.id || subject.name}
                            onClick={() => setActiveSubjectId(subject.id)}
                            className={`relative flex-1 min-w-0 whitespace-nowrap rounded-xl px-5 py-3 text-center text-base font-semibold transition-all ${
                              isActive
                                ? 'bg-primary text-white shadow-lg ring-1 ring-primary/40'
                                : 'bg-white/80 text-text-secondary hover:bg-primary/10 hover:text-primary'
                            }`}
                            aria-pressed={isActive}
                          >
                            <span className="block truncate">
                              {subject.name || 'Subject'}
                            </span>
                            <span
                              className={`pointer-events-none absolute inset-x-4 -bottom-1 h-1 rounded-full bg-white transition-opacity duration-200 ${
                                isActive ? 'opacity-90' : 'opacity-0'
                              }`}
                            />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {subjects.length === 0 ? null : (
                <div className="space-y-6">
                  {activeSubject?.sections.length > 0 ? (
                    activeSubject.sections.map((section) => {
                      const units = sortByOrder(section.units || []);
                      const sectionKey = section.id || section.title || `section-${Math.random()}`;
                      const expanded = isSectionExpanded(sectionKey);
                      const visibleUnits = expanded ? units : units.slice(0, SECTION_PREVIEW_LIMIT);
                      const hasMoreUnits = units.length > SECTION_PREVIEW_LIMIT;

                      return (
                        <div
                          key={sectionKey}
                          className="space-y-5 rounded-2xl border border-border/40 bg-white/90 p-6 shadow-lg backdrop-blur-sm"
                        >
                          <div className="flex items-start gap-3">
                            <span className="mt-1 h-10 w-1.5 rounded-full bg-primary/70" aria-hidden="true" />
                            <div className="space-y-1">
                              <h2 className="text-2xl font-semibold text-primary">
                                {section.title || 'Untitled Section'}
                              </h2>
                              {section.description && (
                                <p className="text-sm text-text-secondary">{section.description}</p>
                              )}
                            </div>
                          </div>

                          {units.length === 0 ? (
                            <div className="rounded-xl border border-dashed border-border px-4 py-8 text-center text-text-secondary">
                              Units coming soon.
                            </div>
                          ) : (
                            <>
                              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                                {visibleUnits.map((unit) => {
                                  const unitSlug = slugifyWithFallback(unit.title, `unit-${unit.id || ''}`);
                                  const thumbnailUrl = resolveUnitThumbnail(unit);

                                  return (
                                    <button
                                      key={unit.id || unitSlug}
                                      type="button"
                                      onClick={() =>
                                        navigate(`/units/${unitSlug}`, {
                                          state: { unitId: unit.id, courseId: resolvedCourseId },
                                        })
                                      }
                                      className="card flex flex-col overflow-hidden bg-surface text-left shadow-md transition-transform duration-200 hover:-translate-y-1 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-primary/30"
                                    >
                                      <UnitThumbnail thumbnailUrl={thumbnailUrl} title={unit.title} />
                                      <div className="flex flex-1 flex-col justify-center p-4">
                                        <h3 className="text-base font-semibold text-primary">
                                          {unit.title || 'Untitled Unit'}
                                        </h3>
                                      </div>
                                    </button>
                                  );
                                })}

                                {hasMoreUnits && (
                                  <div className="pt-3 sm:col-span-2 lg:col-span-3">
                                    <button
                                      type="button"
                                      onClick={() => toggleSectionExpansion(sectionKey)}
                                      className="inline-flex items-center rounded-full bg-primary/10 px-4 py-2 text-sm font-semibold text-primary transition-colors hover:bg-primary/15 focus:outline-none focus:ring-2 focus:ring-primary/40"
                                    >
                                      {expanded ? 'Show less' : 'Show more'}
                                    </button>
                                  </div>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <div className="rounded-lg border border-dashed border-border px-4 py-12 text-center text-text-secondary">
                      Sections will be published soon.
                    </div>
                  )}
                </div>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
};

export default CourseDetailPage;
